<template>
  <d-card
    :id="'product-' + productData.uuid"
    class="mb-4">
    <table class="table mb-0">
      <thead class="bg-primary-light">
        <tr>
          <th scope="col" class="border-0 d-flex" style="min-width: 400px;">
            <span>{{ $t('product.productName') }}</span>
          </th>
          <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.amount') }}</th>
          <th scope="col" class="border-0" style="width: 160px;">{{ $t('price.pricePerItem') }}</th>
          <th scope="col" class="border-0" style="width: 160px;">{{ $t('order.totalCost') }}</th>
          <th scope="col" class="border-0" style="width: 245px;" v-if="!readOnly">{{ $t('ui.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="align-top d-flex is-v-select-wrapper flex-wrap">
            <div class="d-flex w-75 product-name">
              <validation-provider
                v-if="!readOnly"
                rules="required"
                :name="'product-id-' + productData.uuid"
                tag="div"
                class="w-100"
                v-slot="{ errors }">
                <div :class="{ 'form-control w-100': true, 'is-select': true, 'is-invalid': errors.length }">
                  <v-select
                    :options="products"
                    v-model="productData.product_id"
                    :reduce="product => product.id"
                    :filterBy="filterByForProducts"
                    :placeholder="$t('product.selectProduct')"
                    label="code">
                    <template slot="no-options">
                      {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center w-100 normal-white-space">
                        {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center w-100 normal-white-space">
                        {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </validation-provider>
              <span v-else>
                {{ getProductNameById(productData.product_id) }}
              </span>
            </div>
            <div class="w-25">
              <d-button
                v-if="!readOnly"
                class="w-100 ml-2"
                outline
                @click.prevent="$bus.$emit('products-search-popup-open', productData.uuid)">
                <i class="material-icons">search</i>
                {{ $t('ui.search') }}
              </d-button>
            </div>

            <fieldset
              v-if="globalServices.length"
              class="global-services w-100 px-3 pb-2 mt-2">
              <legend class="px-2">
                {{ $t('product.globalServices') }}
              </legend>
              <small
                v-for="(globalService, index) of globalServices"
                :key="'global-service-' + index"
                class="global-services-item d-flex mb-1">
                <template v-if="globalService.id">
                  <span class="w-75 align-self-center">
                    {{ globalService.name }}
                  </span>
                  <d-input-group class="w-25">
                    <d-input
                      :min="0"
                      :step="1"
                      :disabled="readOnly"
                      size="sm"
                      v-model="productData.global_services[globalService.id]"
                      type="number"
                      class="text-right" />
                    <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                  </d-input-group>
                </template>
                <template v-else-if="globalService.temp_id && globalService.serviceData">
                  <span class="w-75 align-self-center">
                    {{ globalService.serviceData.name }}
                  </span>
                  <d-input-group class="w-25">
                    <d-input
                      :min="0"
                      :step="1"
                      :disabled="readOnly"
                      size="sm"
                      v-model="productData.global_services[globalService.temp_id]"
                      type="number"
                      class="text-right" />
                    <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                  </d-input-group>
                </template>
              </small>
            </fieldset>
          </td>
          <td class="align-top">
            <validation-provider
              v-if="!readOnly"
              :rules="'required|min_value:' + (productsMinQty[productData.product_id] ? productsMinQty[productData.product_id] : 1).toString()"
              :name="'product-amount-' + productData.uuid"
              v-slot="{ errors }">
              <d-input-group>
                <d-input
                  :min="1"
                  :state="errors.length ? 'invalid' : null"
                  :readonly="!productData.product_id"
                  v-model="productData.quantity"
                  type="number" />
                <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
              </d-input-group>
            </validation-provider>
            <template v-else>
              {{ productData.quantity }} {{ $t('ui.piecesAbbr') }}<br>
            </template>

            <small
              v-if="productsMinQty[productData.product_id] && productsMinQty[productData.product_id] > 1 && productsMinQty[productData.product_id] > productData.quantity"
              class="d-block text-danger">
              {{ $t('order.productMinQty') }}: {{ productsMinQty[productData.product_id] }} {{ $t('ui.piecesAbbr') }}
            </small>

            <template v-if="this.productData.product_id && !readOnly">
              <small v-if="selectedProductAvailability !== false" class="d-block">
                <span :class="selectedProductAvailability < productData.quantity ? 'text-danger' : 'text-primary'">
                  {{ $t('ui.availability') }}:

                  <template v-if="availabilityRefreshInProgress">
                    <div class="spinner-border spinner-border-sm" role="status"></div>
                  </template>
                  <template v-else>
                    {{ selectedProductAvailability }} {{ $t('ui.piecesAbbr') }}
                  </template>
                </span>
              </small>
              <small v-else class="d-block">
                {{ $t('ui.noAvailabilityData') }}
              </small>
            </template>

            <small
              v-if="!readOnly && productData.quantity > selectedProductAvailability"
              class="d-block w-100">
              <span
                class="text-primary containers-trigger-link"
                style="cursor: pointer;"
                @click="showContainersPopup(productData.product_id, productData.id, productData.quantity - selectedProductAvailability)">
                {{ $t('product.checkAvailability') }}
              </span>
            </small>
          </td>
          <td class="align-top">
            <template v-if="productData.product_id">
              <div v-if="whoOrders === 'my-customer' && !productData.use_custom_partner_price">
                {{ Number(productData.partner_price).toFixed(2) }} {{ selectedCurrencySymbol }}
                <del
                  v-if="whoOrders === 'my-customer' && productData.partner_price !== productData.partner_price_before_discount"
                  style="display: block; line-height: 1;">
                  <small>
                    {{ Number(productData.partner_price_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                  </small>
                </del>
              </div>

              <div v-if="whoOrders === 'my-customer' && productData.use_custom_partner_price">
                <validation-provider
                  rules="required|min_value:0"
                  :name="'product-partner-price-' + productData.uuid"
                  v-slot="{ errors }">
                  <d-input-group
                    :append="selectedCurrencySymbol"
                    class="w-100">
                    <input
                      v-model="productData.partner_price"
                      :invalid="errors.length ? true : null"
                      :readonly="!productData.product_id || readOnly"
                      type="number"
                      step="0.01"
                      class="w-100 text-right form-control" />
                  </d-input-group>
                </validation-provider>
              </div>

              <a
                v-if="whoOrders === 'my-customer' && !productData.use_custom_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useCustomPartnerPrice">
                <small>
                  <i class="material-icons">edit</i>
                  {{ $t('product.useCustomPartnerPrice') }}
                </small>
              </a>

              <a
                v-if="whoOrders === 'my-customer' && productData.use_custom_partner_price && !readOnly"
                href="#"
                class="text-primary"
                @click.prevent="useDefaultPartnerPrice">
                <small>
                  <i class="material-icons">restore</i>
                  {{ $t('product.useDefaultPartnerPrice') }}
                </small>
              </a>

              <div v-if="whoOrders === 'me'">
                {{ Number(productData.price).toFixed(2) }} {{ selectedCurrencySymbol }}
              </div>

              <small
                v-if="whoOrders === 'my-customer'"
                class="d-block">
                {{ $t('order.includedCustomerDiscount') }}:
                <span>
                  {{ Number(productData.partner_price_before_discount - productData.partner_price).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.includedMarkup') }}:
                <span :class="{ 'text-danger': productData.partner_price - productData.price < 0 }">
                  {{ Number(productData.partner_price - productData.price).toFixed(2) }} {{ selectedCurrencySymbol }}<br>
                </span>
                {{ $t('order.yourPrice') }}:
                {{ Number(productData.price).toFixed(2) }} {{ selectedCurrencySymbol }}
              </small>

              <del
                v-if="whoOrders === 'me' && productData.price !== productData.price_before_discount"
                style="display: block; line-height: 1;">
                <small>
                  {{ Number(productData.price_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
                </small>
              </del>

              <small
                v-if="whoOrders === 'me' && productData.product_id && !productData.include_discounts"
                class="text-danger d-block">
                {{ $t('product.notIncludedInDiscounts') }}
              </small>
            </template>
          </td>
          <td class="align-top">
            <template v-if="productData.product_id">
              <template v-if="whoOrders === 'me' && productData.totalPrice && !Number.isNaN(productData.totalPrice)">
                <span class="text-primary">
                  {{ productData.totalPrice }} {{ selectedCurrencySymbol }}
                </span>
              </template>

              <template v-if="whoOrders === 'my-customer' && productData.totalPartnerPrice && !Number.isNaN(productData.totalPartnerPrice)">
                <span class="text-primary">
                  {{ productData.totalPartnerPrice }} {{ selectedCurrencySymbol }}
                </span>
              </template>

              <del
                v-if="whoOrders === 'me' && productData.totalPrice !== productData.totalPriceBeforeDiscount"
                style="display: block; line-height: 1;">
                <small>
                  {{ Number(productData.totalPriceBeforeDiscount).toFixed(2) }} {{ selectedCurrencySymbol }}
                </small>
              </del>

              <small
                v-if="whoOrders === 'my-customer'"
                class="d-block">
                {{ $t('order.includedMarkup') }}:
                <span :class="{ 'text-danger': productData.totalPartnerPrice - productData.totalPrice < 0 }">
                  {{ Number(productData.totalPartnerPrice - productData.totalPrice).toFixed(2) }} {{ selectedCurrencySymbol }}
                </span>
              </small>
            </template>
          </td>
          <td
            v-if="!readOnly"
            class="align-top">
            <d-button
              outline
              @click.prevent="addService()"
              theme="success"
              class="mr-2">
              <i class="material-icons">add</i>
              {{ $t('service.addService') }}
            </d-button>

            <d-button
              outline
              @click.prevent="duplicateProduct()"
              theme="primary"
              class="mr-2"
              :title="$t('ui.duplicate')">
              <i class="material-icons">content_copy</i>
            </d-button>

            <d-button
              outline
              @click.prevent="removeProduct()"
              theme="danger"
              :title="$t('ui.delete')">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>

        <tr v-if="hasDescription || productData.description">
          <td colspan="5">
            <d-form-textarea
              v-if="!readOnly"
              :placeholder="$t('product.enterAdditionalProductDescriptionOptional')"
              :rows="3"
              :max-rows="6"
              :value.prop="productData.description"
              v-model="productData.description"></d-form-textarea>
            <template v-else>
              <em class="text-light">{{ productData.description }}</em>
            </template>
          </td>
        </tr>

        <tr v-if="!readOnly && !hasDescription && !productData.description">
          <td colspan="5">
            <a
              v-if="!hasDescription"
              @click.prevent="hasDescription = true"
              href="#add-desc"
              class="mr-3">
              <small>
                <i class="material-icons">add</i> {{ $t('ui.addDescription') }}
              </small>
            </a>
          </td>
        </tr>

        <tr v-if="productData.services && productData.services.length">
          <td colspan="5">
            <table class="table table-sm table-bordered">
              <thead class="bg-success-light">
                <tr>
                  <th scope="col" class="border-0">
                    <small class="font-weight-bold">{{ $t('service.service') }}</small>
                  </th>
                  <th scope="col" class="border-0" style="width: 100px;">
                    <small class="font-weight-bold">{{ $t('ui.piecesAmount') }}</small>
                  </th>
                  <th
                    scope="col" class="border-0" style="width: 160px;">
                    <small class="font-weight-bold">{{ $t('price.pricePerItemShort') }}</small>
                  </th>
                  <th
                    scope="col" class="border-0" style="width: 160px;">
                    <small class="font-weight-bold">{{ $t('service.setupPrice') }}</small>
                  </th>
                  <th
                    scope="col" class="border-0" style="width: 160px;">
                    <small class="font-weight-bold">{{ $t('order.totalCost') }}</small>
                  </th>
                  <th
                    v-if="!readOnly"
                    scope="col" class="border-0" style="width: 120px;">
                    <small outline class="font-weight-bold">{{ $t('ui.actions') }}</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <request-creator-service
                  v-for="(service, index) of productData.services"
                  :key="'service-item-' + index"
                  :productData="productData"
                  :currencyId="currencyId"
                  :readOnly="readOnly"
                  :serviceData="service"
                  :selectedCurrencySymbol="selectedCurrencySymbol"
                  :services="services"
                  :servicesGroups="servicesGroups"
                  :servicesDiscount="servicesDiscount"
                  :servicesMarkup="servicesMarkup"
                  :servicesSetupMarkup="servicesSetupMarkup"
                  :partnerServicesDiscount="partnerServicesDiscount"
                  :embroideryItems="embroideryItems"
                  :whoOrders="whoOrders"
                  :useCustomerPricing="useCustomerPricing"
                  :orderSetUpInProgress="orderSetUpInProgress" />
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </d-card>
</template>

<script>
import Vue from 'vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import RequestCreatorService from './RequestCreatorService.vue';

export default {
  name: 'request-creator-product',
  props: [
    'currencyId',
    'embroideryItems',
    'readOnly',
    'productData',
    'products',
    'productsMinQty',
    'productsDiscount',
    'partnerProductsDiscount',
    'productsMarkup',
    'servicesDiscount',
    'partnerServicesDiscount',
    'servicesMarkup',
    'servicesSetupMarkup',
    'selectedCurrencySymbol',
    'isOrderView',
    'whoOrders',
    'useCustomerPricing',
    'services',
    'servicesGroups',
    'globalServices',
    'orderSetUpInProgress',
    'availabilityRefreshInProgress'
  ],
  components: {
    'request-creator-service': RequestCreatorService
  },
  computed: {
    selectedProductAvailability () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.product_id);

      if (foundedProduct && foundedProduct.length) {
        if (!foundedProduct[0].availability) {
          return 0;
        }

        return parseInt(foundedProduct[0].availability, 10);
      }

      return false;
    }
  },
  watch: {
    'productData.product_id': function (newValue) {
      if (newValue && !this.orderSetUpInProgress) {
        this.updateProductPrice();
      }
    },
    'productData.quantity': function (newValue) {
      if (newValue !== '' && !this.orderSetUpInProgress) {
        this.updateProductPrice();
      }
    },
    'productData.use_custom_partner_price': function () {
      if (!this.orderSetUpInProgress) {
        this.updateProductPrice();
      }
    },
    'productData.partner_price': function (newValue) {
      if (newValue !== '' && !this.orderSetUpInProgress && this.productData.use_custom_partner_price) {
        this.updateProductPrice();
      }
    },
  },
  data () {
    return {
      hasDescription: false
    };
  },
  mounted () {
    this.$bus.$on('request-creator-global-product-update-price', this.updateProductPrice);
    this.$bus.$on('request-creator-global-initial-product-update-price', this.initialProductPriceCheck);
  },
  methods: {
    filterByForProducts (option, label, search) {
      return (option.code + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    duplicateProduct () {
      this.$bus.$emit('request-creator-duplicate-product', this.productData.uuid);
    },
    removeProduct () {
      this.$bus.$emit('request-creator-remove-product', this.productData.uuid);
    },
    initialProductPriceCheck () {
      this.updateProductPrice(true);
    },
    updateProductPrice (initialCheck = false) {
      let productID = this.productData.product_id;
      let productItem = this.products.filter(product => product.id === productID)[0];
      let currentPriceBeforeDiscount = this.productData.price_before_discount;
      let currentPartnerPriceBeforeDiscount = this.productData.partner_price_before_discount;
      let useCustomPartnerPrice = false;

      if (this.whoOrders === 'my-customer' && this.productData.use_custom_partner_price) {
        useCustomPartnerPrice = true;
      }

      if (!productItem) {
        let fieldsToUpdate = {
          price: '0.00',
          partner_price: '0.00',
          price_before_discount: '0.00'
        };

        if (useCustomPartnerPrice) {
          fieldsToUpdate = {
            price: '0.00',
            price_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_before_discount = '0.00';

          if (useCustomPartnerPrice) {
            fieldsToUpdate.partner_price_before_discount = this.productData.partner_price;
          }
        }

        this.$bus.$emit('request-creator-update-product-fields', {
          uuid: this.productData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'product',
            name: this.productData.name,
            code: this.productData.code,
            old_price: currentPriceBeforeDiscount,
            new_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          currentPartnerPriceBeforeDiscount !== 0 &&
          !useCustomPartnerPrice &&
          this.whoOrders === 'my-customer'
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'product',
            name: this.productData.name,
            code: this.productData.code,
            old_price: currentPartnerPriceBeforeDiscount,
            new_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      this.$bus.$emit('request-creator-update-product-fields', {
        uuid: this.productData.uuid,
        fields: {
          additionalData: JSON.parse(JSON.stringify(productItem)),
          include_discounts: productItem.include_discounts
        }
      });

      let usePartnerPricing = this.whoOrders === 'my-customer' && this.useCustomerPricing;
      let pricingType = usePartnerPricing ? 'customer_prices' : 'prices';
      let productPrice = productItem.prices.filter(price => price.currency_id === this.currencyId)[0];
      let productPartnerPrice = productItem[pricingType].filter(price => price.currency_id === this.currencyId)[0];

      if (!productPrice && !productPartnerPrice) {
        let fieldsToUpdate = {
          price: '0.00',
          partner_price: '0.00',
          price_before_discount: '0.00'
        };

        if (useCustomPartnerPrice) {
          fieldsToUpdate = {
            price: '0.00',
            price_before_discount: '0.00'
          };
        }

        if (this.whoOrders === 'my-customer') {
          fieldsToUpdate.partner_price_before_discount = '0.00';

          if (useCustomPartnerPrice) {
            fieldsToUpdate.partner_price_before_discount = this.productData.partner_price;
          }
        }

        this.$bus.$emit('request-creator-update-product-fields', {
          uuid: this.productData.uuid,
          fields: fieldsToUpdate
        });

        if (initialCheck && currentPriceBeforeDiscount !== 0) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'product',
            name: this.productData.name,
            code: this.productData.code,
            old_price: currentPriceBeforeDiscount,
            new_price: 0,
            source: 'prices'
          });
        }

        if (
          initialCheck &&
          currentPartnerPriceBeforeDiscount !== 0 &&
          !useCustomPartnerPrice &&
          this.whoOrders === 'my-customer'
        ) {
          this.$bus.$emit('request-creator-initial-price-check', {
            type: 'product',
            name: this.productData.name,
            code: this.productData.code,
            old_price: currentPartnerPriceBeforeDiscount,
            new_price: 0,
            source: 'customer_prices'
          });
        }
        return;
      }

      let basePrice = CustomDecimals.roundProperly(parseFloat(productPrice ? productPrice.price : 0));
      let basePartnerPrice = CustomDecimals.roundProperly(parseFloat(productPartnerPrice ? productPartnerPrice.price : 0));

      if (pricingType === 'prices') {
        basePartnerPrice = basePrice;
      }

      if (productPrice && this.productsDiscount > 0 && this.productData.include_discounts) {
        basePrice = CustomDecimals.roundProperly(parseFloat(productPrice.price) - ((this.productsDiscount / 100) * parseFloat(productPrice.price)));
      }

      let partnerPrice = CustomDecimals.roundProperly(parseFloat(basePrice) * ((100 + this.productsMarkup) / 100));

      if (pricingType === 'customer_prices') {
        partnerPrice = CustomDecimals.roundProperly(parseFloat(basePartnerPrice) * ((100 + this.productsMarkup) / 100));
      }

      let fieldsToUpdate = {
        price_before_discount: CustomDecimals.roundProperly(parseFloat(productPrice ? productPrice.price : 0)),
        price: basePrice,
        partner_price: partnerPrice
      };

      if (useCustomPartnerPrice) {
        fieldsToUpdate = {
          price_before_discount: CustomDecimals.roundProperly(parseFloat(productPrice ? productPrice.price : 0)),
          price: basePrice
        };
      }

      if (this.whoOrders === 'my-customer') {
        if (useCustomPartnerPrice) {
          fieldsToUpdate.partner_price_before_discount = this.productData.partner_price;
        } else if (this.partnerProductsDiscount > 0) {
          fieldsToUpdate.partner_price = CustomDecimals.roundProperly(parseFloat(partnerPrice) - ((this.partnerProductsDiscount / 100) * parseFloat(partnerPrice)));
          fieldsToUpdate.partner_price_before_discount = partnerPrice;
        } else {
          fieldsToUpdate.partner_price_before_discount = fieldsToUpdate.partner_price;
        }
      }

      this.$bus.$emit('request-creator-update-product-fields', {
        uuid: this.productData.uuid,
        fields: fieldsToUpdate
      });

      if (
        initialCheck &&
        currentPriceBeforeDiscount !== parseFloat(CustomDecimals.roundProperly(parseFloat(productPrice.price)))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'product',
          name: this.productData.name,
          code: this.productData.code,
          old_price: currentPriceBeforeDiscount,
          new_price: parseFloat(CustomDecimals.roundProperly(parseFloat(productPrice.price))),
          source: 'prices'
        });
      }

      if (
        initialCheck &&
        !useCustomPartnerPrice &&
        this.whoOrders === 'my-customer' &&
        currentPartnerPriceBeforeDiscount !== parseFloat(CustomDecimals.roundProperly(parseFloat(partnerPrice)))
      ) {
        this.$bus.$emit('request-creator-initial-price-check', {
          type: 'product',
          name: this.productData.name,
          code: this.productData.code,
          old_price: currentPartnerPriceBeforeDiscount,
          new_price: parseFloat(CustomDecimals.roundProperly(parseFloat(partnerPrice))),
          source: 'customer_prices'
        });
      }
    },
    getProductNameById (id) {
      let foundedProduct = this.products.filter(product => product.id === id, 10);

      if (foundedProduct.length) {
        return foundedProduct[0].code + ' - ' + foundedProduct[0].name;
      }

      return '';
    },
    addService () {
      this.$bus.$emit('request-creator-add-service', this.productData.uuid);
    },
    showContainersPopup (productID, orderProductID, missingAmount) {
      this.$bus.$emit('container-search-popup-open', parseInt(productID, 10), parseInt(orderProductID, 10), parseInt(missingAmount, 10));
    },
    useCustomPartnerPrice () {
      Vue.set(this.productData, 'use_custom_partner_price', true);
    },
    useDefaultPartnerPrice () {
      Vue.set(this.productData, 'use_custom_partner_price', false);
      this.updateProductPrice();
    }
  },
  beforeDestroy () {
    this.$bus.$off('request-creator-global-product-update-price', this.updateProductPrice);
    this.$bus.$off('request-creator-global-initial-product-update-price', this.initialProductPriceCheck);
  }
}
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}

.product-name {
  justify-content: flex-start;
}

.product {
  &-image {
    height: auto;
    margin: 0 20px 0 0;
    width: 120px;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 120px;
    justify-content: center;
    margin: 0 20px 0 0;
    width: 120px;
  }
}

.global-services {
  border: 1px solid #ddd;
  legend {
    font-size: 14px;
  }
}

.normal-white-space {
  line-height: 1.2;
  white-space: normal;
}
</style>
